import React from "react";
import { Box } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { designColors, StatCard } from "services/constants/designTokens";

// One status-card row for every My Zone > My Requests screen, so they all
// read the same as Team Workspace > Approvals' *StatusCards (same shared
// StatCard, same icon/accent/tint per status, same selected-card border and
// the same 2-up phone layout). Before this, Leave/Corrections used StatCard
// with a different palette while Expense, Advance Salary, Overtime, Off Day
// Duty and the Work Adjustment screens each hand-rolled their own tile.
export const REQUEST_STATUS_CARD_DEFS = {
  All: { label: "All", icon: ListAltIcon, accent: "#1D4ED8", tint: "#EEF4FF" },
  Pending: { label: "Pending", icon: AccessTimeIcon, accent: "#F97316", tint: "#FFF7ED" },
  Approved: { label: "Approved", icon: CheckCircleOutlineIcon, accent: "#16A34A", tint: "#EAFBF1" },
  // Off Day Duty only: an Approved request whose duty date has passed.
  Completed: { label: "Completed", icon: DoneAllIcon, accent: "#1D4ED8", tint: "#EFF8FF" },
  Paid: { label: "Paid", icon: AccountBalanceOutlinedIcon, accent: "#2563EB", tint: "#EFF6FF" },
  Rejected: { label: "Rejected", icon: HighlightOffIcon, accent: "#EF4444", tint: "#FEF2F2" },
  Cancelled: {
    label: "Cancelled",
    icon: RemoveCircleOutlineIcon,
    accent: designColors.slate600,
    tint: designColors.slate100,
  },
};

// Purple totals tile for the screens that also show a (blue) "Paid" card,
// matching Team Workspace's Expense / Advance Salary cards.
const ALL_WITH_PAID_DEF = { ...REQUEST_STATUS_CARD_DEFS.All, accent: "#7C3AED", tint: "#F3E8FF" };

const DEFAULT_KEYS = ["All", "Pending", "Approved", "Rejected", "Cancelled"];

export const requestStatusCardsGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  "@media (max-width:599.95px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
  },
  gap: 1.5,
  mb: 2,
};

// counts: { Pending: n, Approved: n, ... }. total: value for the "All" card
// (defaults to the sum of counts). activeStatus: "" means "All" is selected.
// onSelect receives "" for the "All" card and the status key otherwise.
// labels / details: optional per-key overrides ({ All: "Total Requests" }).
export const RequestStatusCards = ({
  counts,
  total,
  activeStatus,
  onSelect,
  keys = DEFAULT_KEYS,
  labels,
  details,
  sx,
}) => {
  const summedTotal = Object.values(counts || {}).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0,
  );
  const hasPaid = keys.includes("Paid");

  return (
    <Box sx={{ ...requestStatusCardsGridSx, ...sx }}>
      {keys.map((key) => {
        const isAll = key === "All";
        const def = isAll && hasPaid ? ALL_WITH_PAID_DEF : REQUEST_STATUS_CARD_DEFS[key];
        const value = isAll ? "" : key;
        return (
          <StatCard
            activeBorder
            key={key}
            icon={def.icon}
            title={labels?.[key] || def.label}
            value={isAll ? total ?? summedTotal : counts?.[key] || 0}
            detail={details?.[key]}
            accent={def.accent}
            tint={def.tint}
            isActive={(activeStatus || "") === value}
            onClick={onSelect ? () => onSelect(value) : undefined}
          />
        );
      })}
    </Box>
  );
};
